import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn, Tooltip } from "@ronin/ui";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";

interface SortableProps {
  ids: string[];
  /** The new order of all ids. */
  onReorder: (ids: string[]) => void;
  /** The name screen readers hear for an item. */
  label: (id: string) => string;
  layout?: "grid" | "list";
  children: ReactNode;
}

/**
 * spec12 FR9: drag and drop with mouse, touch (the handle) and keyboard (Space to lift,
 * arrows to move, Space to drop), with spoken announcements.
 */
export function Sortable({ ids, onReorder, label, layout = "list", children }: SortableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const position = (id: string | number) => ids.indexOf(String(id)) + 1;

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: {
          draggable:
            "Press Space to pick up. Use the arrow keys to move, Space to drop, Escape to cancel.",
        },
        announcements: {
          onDragStart: ({ active }) => `Picked up ${label(String(active.id))}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `${label(String(active.id))} moved to position ${position(over.id)} of ${ids.length}.`
              : undefined,
          onDragEnd: ({ active, over }) =>
            over
              ? `${label(String(active.id))} dropped at position ${position(over.id)} of ${ids.length}.`
              : `${label(String(active.id))} dropped.`,
          onDragCancel: ({ active }) => `Moving ${label(String(active.id))} was cancelled.`,
        },
      }}
    >
      <SortableContext
        items={ids}
        strategy={layout === "grid" ? rectSortingStrategy : verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}

/** One sortable item; `handle` goes on the drag handle button. */
export function useSortableItem(id: string) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return {
    ref: setNodeRef,
    // Applied through the CSSOM (React style), which the CSP allows.
    style: { transform: CSS.Translate.toString(transform), transition },
    isDragging,
    handle: { ref: setActivatorNodeRef, ...attributes, ...listeners },
  };
}

/** The drag handle: a ghost icon button, `touch-none` so touch drags do not scroll. */
export function DragHandle({
  label,
  handle,
  className,
}: {
  label: string;
  handle: ReturnType<typeof useSortableItem>["handle"];
  className?: string;
}) {
  return (
    <Tooltip content="Drag to move">
      <button
        type="button"
        aria-label={label}
        className={cn(
          "touch-target flex size-7 shrink-0 cursor-grab touch-none items-center justify-center text-dim-foreground hover:bg-muted hover:text-secondary-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring active:cursor-grabbing",
          className,
        )}
        {...handle}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
    </Tooltip>
  );
}
